import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Typography,
    Paper,
    Divider,
    RadioGroup,
    Radio,
    Button,
    FormControlLabel,
    Alert,
} from '@mui/material';

export interface Step5SimData {
    hasCertification: boolean | null;
    certificationSoldInFairs: boolean | null;
    hasMunicipalSeal: boolean | null;
    municipalSealName: string;
    wantsMunicipalSeal: boolean | null;
    knowsHowToCreateSeal: boolean | null;
    referenceYear: number;
}

interface Step5Props {
    onNext: () => void;
    onChange: (data: Step5SimData) => void;
    data: Step5SimData;
    disabled?: boolean;
    onBack?: () => void;
    stepAtual?: number;
    totalSteps?: number;
}

const Step5: React.FC<Step5Props> = ({
    onNext,
    onChange,
    data,
    disabled = false,
    onBack,
}) => {
    const currentYear = new Date().getFullYear();
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    const handleFieldChange = (field: keyof Step5SimData, value: any) => {
        onChange({
            ...data,
            [field]: value,
        });
    };

    const handleSubmit = () => {
        const newErrors: { [key: string]: string } = {};

        if (data.hasCertification === null) newErrors.hasCertification = 'Campo obrigatório.';
        if (data.hasCertification === true && data.certificationSoldInFairs === null) {
            newErrors.certificationSoldInFairs = 'Campo obrigatório.';
        }

        if (data.hasCertification === false) {
            setErrors(newErrors);
            if (Object.keys(newErrors).length === 0) onNext();
            return;
        }

        if (data.hasMunicipalSeal === null) newErrors.hasMunicipalSeal = 'Campo obrigatório.';
        if (data.hasMunicipalSeal && !data.municipalSealName) newErrors.municipalSealName = 'Informe o nome do selo municipal.';
        if (data.wantsMunicipalSeal === null) newErrors.wantsMunicipalSeal = 'Campo obrigatório.';
        if (data.knowsHowToCreateSeal === null) newErrors.knowsHowToCreateSeal = 'Campo obrigatório.';

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onNext();
        }
    };

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Divider sx={{ mb: 2 }} />

                <TextField
                    label="Ano de referência do Questionário"
                    type="number"
                    value={currentYear}
                    fullWidth
                    required
                    disabled
                    sx={{ opacity: 0.5 }}
                    margin="normal"
                />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Preencha todos os campos obrigatórios. Campos condicionais serão exibidos conforme suas respostas.
                </Alert>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            O município possui produto(s) da agricultura familiar com certificação ou identificação? <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <RadioGroup
                            row
                            value={data.hasCertification === null ? '' : data.hasCertification ? 'sim' : 'nao'}
                            onChange={e => handleFieldChange('hasCertification', e.target.value === 'sim')}
                            name="has-certification"
                        >
                            <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                            <FormControlLabel value="nao" control={<Radio />} label="Não" />
                        </RadioGroup>
                        {errors.hasCertification && <Typography color="error">{errors.hasCertification}</Typography>}
                    </Grid>

                    {data.hasCertification && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Esses produtos com certificação ou identificação são vendidos em feiras da agricultura familiar? <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <RadioGroup
                                row
                                value={data.certificationSoldInFairs === null ? '' : data.certificationSoldInFairs ? 'sim' : 'nao'}
                                onChange={e => handleFieldChange('certificationSoldInFairs', e.target.value === 'sim')}
                                name="certification-sold-in-fairs"
                            >
                                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                <FormControlLabel value="nao" control={<Radio />} label="Não" />
                            </RadioGroup>
                            {errors.certificationSoldInFairs && <Typography color="error">{errors.certificationSoldInFairs}</Typography>}
                        </Grid>
                    )}

                    {data.hasCertification && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município possui selo próprio (municipal) para agregação de valor e/ou identificação de produtos da agricultura familiar? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={data.hasMunicipalSeal === null ? '' : data.hasMunicipalSeal ? 'sim' : 'nao'}
                                    onChange={e => handleFieldChange('hasMunicipalSeal', e.target.value === 'sim')}
                                    name="has-municipal-seal"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.hasMunicipalSeal && <Typography color="error">{errors.hasMunicipalSeal}</Typography>}
                            </Grid>

                            {data.hasMunicipalSeal && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Nome do selo municipal"
                                        value={data.municipalSealName}
                                        onChange={e => handleFieldChange('municipalSealName', e.target.value)}
                                        error={!!errors.municipalSealName}
                                        helperText={errors.municipalSealName}
                                        margin="dense"
                                        required
                                        fullWidth
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município tem interesse em criar um selo municipal para agregação de valor e/ou identificação de produtos da agricultura familiar? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={data.wantsMunicipalSeal === null ? '' : data.wantsMunicipalSeal ? 'sim' : 'nao'}
                                    onChange={e => handleFieldChange('wantsMunicipalSeal', e.target.value === 'sim')}
                                    name="wants-municipal-seal"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.wantsMunicipalSeal && <Typography color="error">{errors.wantsMunicipalSeal}</Typography>}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    O município sabe como criar um selo municipal de agregação de valor e/ou identificação de produtos da agricultura familiar? <span style={{ color: 'red' }}>*</span>
                                </Typography>
                                <RadioGroup
                                    row
                                    value={data.knowsHowToCreateSeal === null ? '' : data.knowsHowToCreateSeal ? 'sim' : 'nao'}
                                    onChange={e => handleFieldChange('knowsHowToCreateSeal', e.target.value === 'sim')}
                                    name="knows-how-to-create-seal"
                                >
                                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                                </RadioGroup>
                                {errors.knowsHowToCreateSeal && <Typography color="error">{errors.knowsHowToCreateSeal}</Typography>}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                {onBack && (
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={onBack}
                        disabled={disabled}
                    >
                        Voltar
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    disabled={disabled}
                >
                    Salvar e continuar
                </Button>
            </Box>
        </Box>
    );
};

export default Step5;